import useWindowDimensions from "hooks/useWindowDimensions"
import { useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { Outlet } from 'react-router-dom';
import { useSwipeable } from "react-swipeable"
import useLayout from "hooks/useLayout"
import DesktopTopBar from "components/shared/DesktopTopBar"
import MobileTopBar from "components/shared/MobileTopBar"
import { DeviceSize } from "types"
import FloatingActionButtonStock from "components/buttons/FloatingActionButtonStock"
import LeftSidebar from "components/sidebar/left/LeftSidebar"
import Toast from "components/toast/toast";
import { motion, AnimatePresence } from "motion/react"
import DialogManager from "../shared/DialogManager";
import { Background } from "./background";
import { LayoutChromeProvider, MAIN_SHEET_FOOTER_ID, MAIN_SHEET_HEADER_ACTIONS_ID, useLayoutChromeState } from "./layoutChrome";
import useTargetStorage from "@/hooks/useTargetStorage"

const Layout = () => {

    const { targetStorageDocId: storageId } = useTargetStorage()
    const { categoryId, locationId, customerId, supplierId, productId, transactionId } = useParams<{ 
        categoryId: string
        locationId: string
        customerId: string
        supplierId: string
        productId: string
        transactionId: string
    }>()

    const [isMobile, setIsMobile] = useState(false)
    const location = useLocation()
    const isStorageRoute = location.pathname === '/storage' || location.pathname.startsWith('/storage/')
    const isBareMobileBackground = location.pathname === '/storages' || location.pathname === '/notifications' || location.pathname === '/access-invitations'
    const { hideMainSheet, title, value: layoutChrome } = useLayoutChromeState()
    const {width} = useWindowDimensions()

    const {
        hideDialog,
        hideSidebar,
        showSidebar,
        showDialogState,
        galleryFullscreen,
        toasts
    } = useLayout()

    const {
        deviceSize
    } = useWindowDimensions()
    
    // Handle swipe events
    const handlers = useSwipeable({
        onSwipedLeft: (eventData) => {
            const target = eventData.event.target as HTMLElement;
            // Skip if swiping on specific elements
            if (target.closest('.no-swipe') || target.closest('input') || target.closest('button')) {
                return;
            }
            !showDialogState && isMobile && hideSidebar()
        },
        onSwipedRight: (eventData) => {
            const target = eventData.event.target as HTMLElement;
            if (target.closest('.no-swipe') || target.closest('input') || target.closest('button')) {
                return;
            }
            !showDialogState && isMobile && showSidebar()
        },
        trackMouse: true,
        delta: width as number / 4
    });

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1024)
        }
        handleResize()
        window.addEventListener("resize", handleResize)
        return () => {
            window.removeEventListener("resize", handleResize)
        }
    }, [] )

    useEffect(() => {
        if (isMobile) hideSidebar()
        hideDialog()
    }, [location, isMobile, hideSidebar, hideDialog])

    return (
        <LayoutChromeProvider value={layoutChrome}>
        <div
            className={`
                layout
                fixed
                top-0
                bottom-0
                left-0
                right-0
            `}
            {...handlers}
        >
            <Background />

            {/* Sidebars */}
            <LeftSidebar 
                className="lg:visible lg:w-60" 
                storageId={storageId}
                categoryId={categoryId}
                locationId={locationId}
                customerId={customerId}
                productId={productId}
            />

            {/* Top bar */}
            {deviceSize === DeviceSize.LG ?
                <DesktopTopBar 
                    className={`invisible lg:visible lg:left-60 lg:right-60 ${showDialogState || galleryFullscreen ? '' : 'z-[100]'}`}
                    storageId={storageId}
                    categoryId={categoryId}
                    locationId={locationId}
                    customerId={customerId}
                    supplierId={supplierId}
                    productId={productId}
                    transactionId={transactionId}
                /> : 
                <MobileTopBar 
                    storageId={storageId}
                    categoryId={categoryId}
                    customerId={customerId}
                    productId={productId}
                    transactionId={transactionId}
                />
            }

            {/* Toasts */}
            <div className="fixed bottom-0 z-[120] mb-2 -translate-x-1/2 left-1/2 w-full max-w-md flex flex-col items-center space-y-2">
                <AnimatePresence initial={false}>
                {toasts.map((toast, index) => (
                    <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                        key={index}
                    >
                        <Toast text={toast.text} colorScheme={toast.colorScheme} />
                    </motion.div>
                ))}
                </AnimatePresence>
            </div>

            {/* Main content warpper */}
            <div className={`
                fixed
                top-14
                lg:top-16
                z-10

                left-0
                right-0
                lg:left-60
                lg:right-60

                bottom-0
                flex
                flex-col
                min-h-0
                overflow-hidden
                ${hideMainSheet ? '' : isStorageRoute || isBareMobileBackground ? 'lg:rounded-t-md lg:shadow-xl' : 'rounded-t-md shadow-xl'}
            `}>
                {/* Background + scroll holder are the same box so the scrollbar cannot outgrow the panel */}
                <div
                    id="layout-background"
                    className={`
                        relative
                        min-h-0
                        flex-1
                        overflow-x-hidden
                        overflow-y-auto
                        md:[scrollbar-width:thin]
                        md:[scrollbar-color:#d1d5db_transparent]
                        md:[&::-webkit-scrollbar]:w-2
                        md:[&::-webkit-scrollbar]:bg-transparent
                        md:[&::-webkit-scrollbar-track]:bg-transparent
                        md:[&::-webkit-scrollbar-thumb]:rounded-full
                        md:[&::-webkit-scrollbar-thumb]:bg-gray-300
                        w-full
                        min-w-0
                        mx-auto
                        flex
                        flex-col
                        items-stretch
                        2xl:items-center
                        lg:absolute
                        lg:top-0
                        lg:right-0
                        lg:left-0
                        lg:bottom-4
                        lg:h-auto
                        lg:flex-none
                        lg:rounded-md
                        lg:p-4
                        lg:shadow-md
                        bg-white
                        ${isStorageRoute || isBareMobileBackground
                            ? 'lg:border lg:border-solid lg:border-gray-300 lg:bg-[linear-gradient(0deg,#e8e8e8,#cddeed)] dark:lg:bg-[linear-gradient(0deg,#465869,#182f4e)]'
                            : 'border border-solid border-gray-300 bg-[linear-gradient(0deg,#e8e8e8,#cddeed)] dark:bg-[linear-gradient(0deg,#465869,#182f4e)]'}
                    `}
                >
                    {/* Main content */}
                    <div className={`
                        w-full
                        min-w-0
                        max-w-none
                        2xl:max-w-[1024px]
                        flex
                        flex-col
                        ${hideMainSheet ? 'min-h-full shrink-0 space-y-4' : 'shrink-0 bg-white lg:overflow-hidden lg:shadow-md lg:rounded-md'}
                    `}>
                        {!hideMainSheet && title && (
                            <div className="
                                border-b-[1px]
                                h-20
                                px-4
                                flex
                                flex-row
                                gap-x-4
                                justify-between
                                items-center
                                bg-gray-100
                                border-b-gray-200
                                shrink-0
                                lg:rounded-t-md
                            ">
                                <h2 className="text-gray-800 truncate min-w-0">{title}</h2>
                                <div id={MAIN_SHEET_HEADER_ACTIONS_ID} className="flex items-center gap-x-4 shrink-0" />
                            </div>
                        )}
                        <div className={`
                            flex
                            flex-col
                            space-y-4
                            ${hideMainSheet ? '' : 'px-0 lg:px-4 py-4'}
                        `}>
                            <Outlet />
                        </div>
                        {!hideMainSheet && (
                            <div
                                id={MAIN_SHEET_FOOTER_ID}
                                className="border-t-[1px] px-4 py-3 bg-gray-100 border-t-gray-200 shrink-0 empty:hidden"
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Dialogs & floating buttons */}
            <DialogManager
                storageId={storageId}
                categoryId={categoryId}
                locationId={locationId}
                customerId={customerId}
                supplierId={supplierId}
                productId={productId}
                transactionId={transactionId}
            />
            {isStorageRoute && storageId ? <FloatingActionButtonStock storageId={storageId} /> : null}
        </div>
        </LayoutChromeProvider>
    )
}

export default Layout

